import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { Smartphone, Printer, Save, Apple, Globe, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AppQR = () => {
  const [links, setLinks] = useState({ ios_url: '', android_url: '', web_url: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const qrRef = useRef(null);

  // QR points to the smart-redirect URL
  const qrTarget = `${API}/api/app-links/redirect`;

  useEffect(() => {
    axios.get(`${API}/api/app-links`).then(r => {
      const d = r.data || {};
      const stored = d.web_url || '';
      // Ignore any stale preview/dev URL — always default to THIS site's portal
      const isStale = !stored || /preview\.emergentagent|localhost|127\.0\.0\.1/i.test(stored);
      setLinks({
        ios_url: d.ios_url || '',
        android_url: d.android_url || '',
        web_url: isStale ? `${window.location.origin}/employee-portal/login` : stored,
      });
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/app-links`, links);
      toast({ title: 'App links saved', description: 'QR will now redirect using these URLs.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url; a.download = 'Vynce_App_QR.png';
    document.body.appendChild(a); a.click(); a.remove();
  };

  const printPoster = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Vynce App QR Poster</title>
        <style>
          @page { size: A4; margin: 0; }
          html, body { margin: 0; padding: 0; }
          body {
            font-family: -apple-system, "Segoe UI", Helvetica, Arial, sans-serif;
            color: #111;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .poster {
            width: 210mm; height: 297mm;
            display: flex; flex-direction: column;
            box-sizing: border-box; padding: 18mm 16mm;
            background: linear-gradient(180deg, #faf5ff 0%, #ffffff 60%, #fdf4ff 100%);
          }
          .brand {
            text-align: center; margin-bottom: 6mm;
          }
          .brand-mark {
            font-size: 36px; font-weight: 800;
            background: linear-gradient(90deg, #7c3aed, #d946ef);
            -webkit-background-clip: text; background-clip: text;
            color: transparent;
            letter-spacing: -1px;
          }
          .brand-sub {
            font-size: 13px; color: #6b7280;
            margin-top: 2mm; text-transform: uppercase; letter-spacing: 4px;
          }
          h1 {
            font-size: 30px; line-height: 1.15; margin: 4mm 0 2mm; text-align: center; color: #111827;
          }
          .lead {
            text-align: center; color: #4b5563; font-size: 14px; max-width: 150mm; margin: 0 auto 8mm;
          }
          .qr-wrap {
            display: flex; justify-content: center; align-items: center;
            margin: 4mm auto 6mm; padding: 6mm;
            background: white; border-radius: 6mm;
            box-shadow: 0 4mm 10mm rgba(124, 58, 237, 0.15);
            border: 2px solid #ede9fe;
          }
          .qr-wrap img { width: 80mm; height: 80mm; display: block; }
          .scan-cta {
            text-align: center; font-weight: 700; color: #6d28d9; font-size: 16px; margin-bottom: 7mm;
          }
          .steps {
            display: grid; grid-template-columns: 1fr 1fr; gap: 6mm; margin-top: 2mm;
          }
          .step {
            background: white; border: 1px solid #e5e7eb; border-radius: 4mm;
            padding: 5mm; box-shadow: 0 1mm 3mm rgba(0,0,0,0.04);
          }
          .step-head {
            display: flex; align-items: center; gap: 3mm; margin-bottom: 3mm;
            font-weight: 700; color: #111827; font-size: 14px;
          }
          .step-icon {
            width: 9mm; height: 9mm; border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            font-size: 16px; color: white;
          }
          .step-icon.ios { background: #111827; }
          .step-icon.android { background: #16a34a; }
          .step ol { margin: 0; padding-left: 5mm; color: #374151; font-size: 11px; line-height: 1.55; }
          .footer {
            margin-top: auto; text-align: center; color: #9ca3af; font-size: 10px;
            padding-top: 6mm; border-top: 1px dashed #d1d5db;
          }
        </style>
      </head>
      <body>
        <div class="poster">
          <div class="brand">
            <div class="brand-mark">Vynce HRMS</div>
            <div class="brand-sub">Employee App</div>
          </div>
          <h1>Get the Vynce app on your phone</h1>
          <p class="lead">Apply for leave, view payslips, set your clock-in PIN, and more — all from your pocket.</p>

          <div class="qr-wrap"><img src="${dataUrl}" alt="Vynce App QR"/></div>
          <div class="scan-cta">Scan with your phone camera to get started</div>

          <div class="steps">
            <div class="step">
              <div class="step-head"><span class="step-icon ios"></span> iPhone / iPad</div>
              <ol>
                <li>Open the <b>Camera</b> app and point it at the QR code.</li>
                <li>Tap the yellow link banner to open the page in Safari.</li>
                <li>Tap the <b>Share</b> button → <b>Add to Home Screen</b>.</li>
                <li>Vynce icon now appears on your home screen.</li>
              </ol>
            </div>
            <div class="step">
              <div class="step-head"><span class="step-icon android"></span> Android</div>
              <ol>
                <li>Open <b>Google Lens</b> or your <b>Camera</b> app.</li>
                <li>Point at the QR and tap the link.</li>
                <li>Open in Chrome → menu (⋮) → <b>Add to Home screen</b>.</li>
                <li>Tap <b>Install</b> when prompted for an app-like experience.</li>
              </ol>
            </div>
          </div>

          <div class="footer">Need help? Contact HR · vynce.hrms · ${new Date().getFullYear()}</div>
        </div>
        <script>
          window.onload = () => { setTimeout(() => { window.print(); window.onafterprint = () => window.close(); }, 250); };
        </script>
      </body>
      </html>
    `;
    const w = window.open('', '_blank');
    if (!w) { toast({ title: 'Pop-up blocked', description: 'Allow pop-ups to print the poster.', variant: 'destructive' }); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  if (loading) return <div className="p-8 text-gray-500">Loading…</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="app-qr-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-violet-100 p-3 rounded-xl"><Smartphone className="h-6 w-6 text-violet-700" /></div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff App QR</h1>
          <p className="text-sm text-gray-500">Smart-detects iPhone, Android, or desktop and routes accordingly.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Left: QR Preview */}
        <Card>
          <CardHeader><CardTitle className="text-base">Scan this QR</CardTitle></CardHeader>
          <CardContent>
            <div ref={qrRef} className="flex justify-center bg-white p-6 rounded-xl border-2 border-violet-100 shadow-sm">
              <QRCodeCanvas value={qrTarget} size={260} level="H" includeMargin={true} />
            </div>
            <p className="text-[11px] text-gray-500 text-center mt-2 break-all">
              QR target: <code>{qrTarget}</code>
            </p>
            <div className="flex gap-2 mt-4">
              <Button onClick={printPoster} className="flex-1 bg-violet-600 hover:bg-violet-700" data-testid="print-poster">
                <Printer className="h-4 w-4 mr-1" /> Print A4 Poster
              </Button>
              <Button onClick={downloadQR} variant="outline" data-testid="download-qr">
                <Download className="h-4 w-4 mr-1" /> PNG
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Right: Configure URLs */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Configure destination links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-gray-500">
              The QR routes based on the device that scans it. Leave a field blank to fall back to the web portal.
            </p>

            <div>
              <Label className="flex items-center gap-1.5"><Apple className="h-4 w-4" /> iOS App Store URL</Label>
              <Input
                placeholder="https://apps.apple.com/app/idXXXXXXXXX"
                value={links.ios_url}
                onChange={e => setLinks({ ...links, ios_url: e.target.value })}
                data-testid="ios-url"
              />
              <p className="text-[10px] text-gray-400 mt-1">Optional — leave blank if no native iOS app.</p>
            </div>

            <div>
              <Label className="flex items-center gap-1.5">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85a.637.637 0 0 0-.83.22l-1.88 3.24a11.5 11.5 0 0 0-8.94 0L5.65 5.67a.643.643 0 0 0-.87-.2c-.28.18-.37.54-.22.83L6.4 9.48A10.81 10.81 0 0 0 1 18h22a10.81 10.81 0 0 0-5.4-8.52zM7 15.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm10 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z"/></svg>
                Google Play URL
              </Label>
              <Input
                placeholder="https://play.google.com/store/apps/details?id=..."
                value={links.android_url}
                onChange={e => setLinks({ ...links, android_url: e.target.value })}
                data-testid="android-url"
              />
              <p className="text-[10px] text-gray-400 mt-1">Optional — leave blank if no native Android app.</p>
            </div>

            <div>
              <Label className="flex items-center gap-1.5"><Globe className="h-4 w-4" /> Web Portal URL (fallback)</Label>
              <Input
                placeholder={`${window.location.origin}/employee-portal/login`}
                value={links.web_url}
                onChange={e => setLinks({ ...links, web_url: e.target.value })}
                data-testid="web-url"
              />
              <p className="text-[10px] text-gray-400 mt-1">
                Used for desktops and as a fallback. Tip: this defaults to <code>/employee-portal/login</code> on this site — staff can install it from there as a Progressive Web App.
              </p>
            </div>

            <Button onClick={save} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-app-links">
              <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardContent className="p-4 text-xs text-gray-500 space-y-1">
          <p><b>How it works:</b> the QR points to <code>/api/app-links/redirect</code>. When scanned:</p>
          <ul className="list-disc pl-5 space-y-0.5">
            <li>iPhone / iPad → opens iOS App Store URL (or web portal if blank)</li>
            <li>Android phone → opens Google Play URL (or web portal if blank)</li>
            <li>Desktop / other → opens web portal URL</li>
          </ul>
          <p className="pt-2">Update the URLs anytime — the QR poster you printed continues to work, since the routing is decided at scan-time on the server.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AppQR;
